import { useDatoClient } from '@src/stores-v2/StoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useRef, useState } from 'react';
import { useQuerySubscription } from 'react-datocms/use-query-subscription';

import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { QueryListenerOptions } from 'react-datocms/use-query-subscription';

const logger = LogManager.getLogger('hook:DatoQuerySubscription');

const STALENESS_CHECK_INTERVAL_MS = 10_000;
const STALENESS_THRESHOLD_MS = 30_000; // 30 seconds
const CONNECTION_TIMEOUT_MS = 10_000;
const POLL_INTERVAL_MS = 15_000;

export function useDatoQuerySubscription<QueryResult, QueryVariables>(
  options: Omit<
    QueryListenerOptions<QueryResult, QueryVariables>,
    'environment' | 'token'
  > & {
    query: TypedDocumentNode<QueryResult, QueryVariables> | string;
  },
): ReturnType<typeof useQuerySubscription<QueryResult, QueryVariables>> {
  const datoClientStore = useDatoClient();

  // 1. Existing subscription (unchanged behavior)
  const subscription = useQuerySubscription<QueryResult, QueryVariables>({
    ...options,
    environment: datoClientStore.environment,
    token: datoClientStore.gqlAuthToken,
  });

  // 2. Polling state
  const [pollData, setPollData] = useState<QueryResult | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  // 3. Staleness tracking
  const lastUpdateTimeRef = useRef<number>(Date.now());
  const connectionUnhealthySinceRef = useRef<number | null>(null);

  // Stabilize variables for useEffect deps
  const variablesKey = JSON.stringify(options.variables);
  const querySnippet =
    typeof options.query === 'string'
      ? options.query.slice(0, 50)
      : '[GraphQL Document]';

  // Track subscription health, reset staleness, and detect recovery
  const prevDataRef = useRef<QueryResult | undefined>(undefined);
  useEffect(() => {
    // Track connection health
    if (subscription.status === 'connected') {
      connectionUnhealthySinceRef.current = null;
    } else if (!connectionUnhealthySinceRef.current) {
      connectionUnhealthySinceRef.current = Date.now();
    }

    // Reset lastUpdateTime when subscription data changes
    const dataChanged = subscription.data !== prevDataRef.current;
    prevDataRef.current = subscription.data;

    if (subscription.data && dataChanged) {
      lastUpdateTimeRef.current = Date.now();
    }

    // If subscription recovered (fresh data while connected), stop polling
    if (
      isPolling &&
      subscription.status === 'connected' &&
      subscription.data &&
      dataChanged
    ) {
      logger.info('Subscription recovered, stopping polling', { querySnippet });
      setIsPolling(false);
      setPollData(null);
    }
  }, [subscription.data, subscription.status, isPolling]);

  // 4. Staleness check interval
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const stale = now - lastUpdateTimeRef.current > STALENESS_THRESHOLD_MS;
      const disconnected =
        connectionUnhealthySinceRef.current !== null &&
        now - connectionUnhealthySinceRef.current > CONNECTION_TIMEOUT_MS;

      if ((stale || disconnected) && !isPolling) {
        const reason = stale ? 'stale' : 'disconnected';
        logger.warn(`Activating polling fallback (reason: ${reason})`, {
          querySnippet,
        });
        setIsPolling(true);
      }
    }, STALENESS_CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isPolling]);

  // 5. Polling fetch
  useEffect(() => {
    if (!isPolling) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const result = await datoClientStore.gqlClient.request<QueryResult>(
          options.query as string,
          options.variables as Record<string, unknown>,
        );
        if (!cancelled) {
          logger.info('Poll result received', {
            query: querySnippet,
            keys: Object.keys((result as object) ?? {}),
          });
          setPollData(result);
          lastUpdateTimeRef.current = Date.now();
        } else {
          logger.info(
            'Poll result received but component is unmounted, ignoring',
            { querySnippet },
          );
        }
      } catch (err) {
        logger.warn('Poll fetch failed', {
          querySnippet,
          error: extractErrorInfo(err),
        });
      }
    };

    poll(); // Immediate first poll
    const interval = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isPolling, options.query, variablesKey]);

  // 6. Return merged result — poll data takes precedence when polling is active
  return {
    status: subscription.status,
    error: subscription.error,
    data: (isPolling && pollData
      ? pollData
      : subscription.data) as typeof subscription.data,
  };
}
